import React, { useState, useEffect } from "react";
import {
  Calendar,
  Users,
  TrendingUp,
  Clock,
  CheckCircle,
  BarChart3,
  Zap,
  ArrowRight,
  Menu,
  X,
  ChevronRight,
  ChevronDown,
  Shield,
  MessageSquare,
  FileText,
  Store,
  Layers,
  Play,
  Package,
  Wallet,
  UserCog,
  ClipboardList,
  Workflow,
  Megaphone,
  CarFront,
  Landmark,
  Bot
} from "lucide-react";
import { USE_CASES } from "../../data/useCasesData";
import {
  BsInstagram,
  BsFacebook,
  BsLinkedin,
  BsYoutube,
  BsTwitterX,
  BsWhatsapp,
  BsGlobe2,
  BsPeopleFill,
  BsCashStack,
  BsGraphUpArrow
} from "react-icons/bs";
import BrandLogo from "../../components/BrandLogo/BrandLogo";
import { useNavigate, Link, useLocation } from "react-router-dom";
import "./home.css";
import { Helmet } from "react-helmet-async";
import * as apicall from "../../components/Apicall";
import { apiUrl } from "../../constants/Constants";
import { websiteTokens } from "./websiteTokens";
import { useWebsiteTheme } from "./useWebsiteTheme";
import ThemeToggle from "./components/ThemeToggle";
import DeviceCollage from "./components/DeviceCollage";
import ModuleShowcase, { EnterpriseBadge } from "./components/ModuleShowcase";
import { MODULES, HERO_SHOT, CORE_SHOTS } from "../../data/marketingModules";
import {
  PRICING_PLANS,
  PRICING_FAQS,
  COMPARISON_ROWS,
  ENTERPRISE_SUITE_FEATURES,
  getPlanPrice as resolvePlanPrice,
  getOriginalPrice,
  getAnnualBilledNote
} from "./pricingData";

export default function EzeitProHomepage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeMetric, setActiveMetric] = useState(0);
  const { isDark, toggleTheme } = useWebsiteTheme();
  const [isMobile, setIsMobile] = useState(false);
  const [annual, setAnnual] = useState(false);
  const [pricingCountry, setPricingCountry] = useState("AE");
  const [openFaq, setOpenFaq] = useState(null);
  const [megaMenuOpen, setMegaMenuOpen] = useState(false);
  const [mobileUseCasesOpen, setMobileUseCasesOpen] = useState(false);
  const megaMenuTimer = React.useRef(null);

  const openMega = () => {
    clearTimeout(megaMenuTimer.current);
    setMegaMenuOpen(true);
  };
  const closeMega = () => {
    megaMenuTimer.current = setTimeout(() => setMegaMenuOpen(false), 160);
  };
  const [, setHoveredPlan] = useState(null);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [demoForm, setDemoForm] = useState({
    name: "",
    email: "",
    message: "",
    date: "",
    time: ""
  });
  const [demoSubmitting, setDemoSubmitting] = useState(false);
  const [demoSubmitted, setDemoSubmitted] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.replace("#", ""));
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  }, [location.hash]);

  const goToContact = () =>
    document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });

  const openWhatsAppChat = () => {
    window.open("https://wa.me/971561303601", "_blank", "noopener,noreferrer");
  };

  const handleDemoSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!demoForm.name.trim() || !demoForm.email.trim()) {
      return alert("Please enter your name and email to schedule a demo.");
    }
    setDemoSubmitting(true);
    try {
      const res = await fetch(`${apiUrl}api/v1/request-demo`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          from_name: demoForm.name,
          from_email: demoForm.email,
          demo_message: demoForm.message,
          preferred_date: demoForm.date,
          preferred_time: demoForm.time
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to send request");
      setDemoSubmitted(true);
      setDemoForm({ name: "", email: "", message: "", date: "", time: "" });
    } catch (err) {
      alert(
        "Failed to send. Please try again or email us directly at sales@ezeitpro.com"
      );
    } finally {
      setDemoSubmitting(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    console.log(token, "token");
    if (!token) return;
    const planMap = { free: "starter", pro: "pro", enterprise: "enterprise" };
    apicall
      .get("ezpMyProfile")
      .then((res) => {
        console.log(res, "res");
        if (res?.success && res.data?.[0]?.plan_id) {
          setCurrentPlan(planMap[res.data[0].plan_id.toLowerCase()] || null);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!showVideoModal) return;
    const onKey = (e) => {
      if (e.key === "Escape") setShowVideoModal(false);
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [showVideoModal]);

  // Plans, prices, comparison rows and FAQs live in ./pricingData (shared with /pricing).
  const pricingPlans = PRICING_PLANS;
  const pricingFaqs = PRICING_FAQS;
  const getPlanPrice = (plan) => resolvePlanPrice(plan, pricingCountry, annual);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const interval = setInterval(
      () => setActiveMetric((prev) => (prev + 1) % 3),
      2500
    );
    return () => clearInterval(interval);
  }, []);

  const d = websiteTokens(isDark);

  const card = (extra = {}) => ({
    background: d.surface,
    borderRadius: "16px",
    border: `1px solid ${d.border}`,
    boxShadow: d.sh2,
    transition: "all 0.25s ease",
    ...extra
  });

  const hoverCard = (e, enter) => {
    e.currentTarget.style.boxShadow = enter ? d.sh8 : d.sh2;
    e.currentTarget.style.transform = enter
      ? "translateY(-4px)"
      : "translateY(0)";
  };

  const pill = {
    display: "inline-block",
    background: d.chipBg,
    color: d.chipText,
    padding: "5px 14px",
    borderRadius: "20px",
    fontSize: "11px",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    marginBottom: "14px"
  };

  const btnP = {
    background: d.primary,
    color: d.onPrimary,
    border: "none",
    borderRadius: "28px",
    padding: "13px 28px",
    fontWeight: 600,
    fontSize: "14px",
    cursor: "pointer",
    boxShadow: `0 3px 8px ${isDark ? "rgba(233,30,99,0.45)" : "rgba(194,24,91,0.3)"}`,
    transition: "all 0.2s",
    display: "inline-flex",
    alignItems: "center",
    gap: "8px"
  };

  const btnO = {
    background: "transparent",
    color: d.primary,
    border: `2px solid ${d.primary}`,
    borderRadius: "28px",
    padding: "11px 26px",
    fontWeight: 600,
    fontSize: "14px",
    cursor: "pointer",
    transition: "all 0.2s",
    display: "inline-flex",
    alignItems: "center",
    gap: "8px"
  };

  const metrics = [
    { value: "25%", label: "More Bookings", sub: "Multi-channel access" },
    { value: "30%", label: "Fewer No-Shows", sub: "Automated reminders" },
    { value: "20-30%", label: "Crew Efficiency", sub: "Smart scheduling" }
  ];

  const features = [
    {
      icon: CarFront,
      title: "Dealership Management (DMS)",
      desc: "Vehicle sales, workshop repair orders, parts, warranty and after-sales in one system",
      enterprise: true
    },
    {
      icon: Landmark,
      title: "Finance & Accounting",
      desc: "Auto-posted journals, UAE VAT 201, bank reconciliation and financial statements",
      enterprise: true
    },
    {
      icon: Bot,
      title: "AI Assistant, 24x7",
      desc: "Answers customers on your WhatsApp number day and night, and hands over to your team when needed",
      enterprise: true
    },
    {
      icon: Calendar,
      title: "Multi-Channel Booking",
      desc: "Capture every enquiry across web, app, Google, and walk-ins"
    },
    {
      icon: Users,
      title: "Crew Scheduling",
      desc: "Visual dashboard for smart job assignment and availability tracking"
    },
    {
      icon: MessageSquare,
      title: "SMS & WhatsApp",
      desc: "Automated confirmations, reminders, and post-service follow-ups"
    },
    {
      icon: FileText,
      title: "TAX Invoicing",
      desc: "Compliant invoicing with real-time payment tracking and reporting"
    },
    {
      icon: BarChart3,
      title: "Advanced Analytics",
      desc: "Actionable insights on revenue, bookings, and staff performance"
    },
    {
      icon: Store,
      title: "UtilStop Marketplace",
      desc: "Optional demand generation through an open customer marketplace"
    },
    {
      icon: Package,
      title: "Inventory, PO & GRN",
      desc: "Track stock and low-stock alerts on Pro. Add purchase orders, goods receipts (GRN), multi-warehouse and stock take on Enterprise"
    },
    {
      icon: Wallet,
      title: "Expense Management",
      desc: "Record and track company overheads such as rent, utilities and office bills in one place"
    },
    {
      icon: UserCog,
      title: "HRMS & Employees",
      desc: "Employee records, attendance, leave, rosters, documents and an org chart in one HR workspace"
    },
    {
      icon: ClipboardList,
      title: "Quotes, Bids & Subscriptions",
      desc: "Send quotations that convert to bookings, answer UtilStop bid requests and run recurring subscriptions"
    },
    {
      icon: Workflow,
      title: "Workflows, Tasks & Resources",
      desc: "Split jobs into steps with My Tasks for field crew, and book shared resources and assets"
    },
    {
      icon: Megaphone,
      title: "Leads, Campaigns & Channels",
      desc: "Capture leads, run campaigns and manage social conversations from one inbox (Enterprise)"
    }
  ];

  const problems = [
    {
      icon: Calendar,
      title: "Fragmented Bookings",
      desc: "Calls, WhatsApp, and walk-ins handled manually with no central system",
      stat: "30%",
      statLabel: "Missed Bookings"
    },
    {
      icon: Clock,
      title: "Scheduling Conflicts",
      desc: "Manual scheduling leads to double-bookings, idle staff, and missed jobs",
      stat: "25%",
      statLabel: "No-Show Rate"
    },
    {
      icon: FileText,
      title: "Billing Delays",
      desc: "Inconsistent pricing, delayed invoicing, and poor payment visibility",
      stat: "40%",
      statLabel: "Revenue Delayed"
    }
  ];

  const bookingChannels = [
    {
      title: "Shareable Web Booking Links",
      desc: "Send customers directly to your booking page via any channel"
    },
    {
      title: "Embedded Booking Engine",
      desc: "Integrate seamlessly into your existing website"
    },
    {
      title: "Google Business Profile",
      desc: "Let customers book directly from Google Search and Maps"
    },
    {
      title: "Admin & Walk-In Bookings",
      desc: "Front-desk or on-the-spot bookings captured instantly"
    }
  ];

  const whyChoose = [
    { num: "01", text: "Go live within hours — easy setup, no IT required" },
    { num: "02", text: "Built exclusively for SMB service businesses" },
    { num: "03", text: "Multi-channel booking with automated communications" },
    { num: "04", text: "Tax compliant invoicing and payment tracking" },
    {
      num: "05",
      text: "Actionable analytics and optional UtilStop marketplace"
    }
  ];

  const colsStyle = (cols, mCols = 1) => ({
    display: "grid",
    gridTemplateColumns: isMobile
      ? `repeat(${mCols}, 1fr)`
      : `repeat(${cols}, 1fr)`,
    gap: "24px"
  });

  const twoCol = (reverse = false) => ({
    display: "grid",
    gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
    gap: "56px",
    alignItems: "center",
    ...(reverse && !isMobile ? { direction: "rtl" } : {})
  });

  const crewMock = (
    <div
      style={{ ...card(), padding: "28px", boxShadow: d.sh8, direction: "ltr" }}
    >
      <div
        style={{
          fontSize: "11px",
          color: d.textMuted,
          textTransform: "uppercase",
          letterSpacing: "0.1em",
          fontWeight: 600,
          marginBottom: "18px"
        }}
      >
        Crew Dashboard
      </div>
      {[
        { name: "Alex", status: "Available", color: "#2E7D32", bg: "#E8F5E9" },
        {
          name: "Jordan",
          status: "On Job",
          color: "#E65100",
          bg: isDark ? "rgba(230,81,0,0.15)" : "#FFF3E0"
        },
        {
          name: "Sam",
          status: "Break",
          color: "#616161",
          bg: isDark ? "rgba(97,97,97,0.15)" : "#F5F5F5"
        },
        { name: "Taylor", status: "Available", color: "#2E7D32", bg: "#E8F5E9" }
      ].map((c, i) => (
        <div
          key={i}
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "12px 14px",
            borderRadius: "10px",
            background: d.bgAlt,
            marginBottom: "8px",
            border: `1px solid ${d.border}`
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div
              style={{
                width: "36px",
                height: "36px",
                borderRadius: "50%",
                background: d.primaryCont,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "14px",
                fontWeight: 700,
                color: d.primary
              }}
            >
              {c.name[0]}
            </div>
            <span style={{ fontSize: "14px", fontWeight: 500, color: d.text }}>
              {c.name}
            </span>
          </div>
          <span
            style={{
              fontSize: "12px",
              fontWeight: 500,
              padding: "4px 10px",
              borderRadius: "8px",
              background: c.bg,
              color: c.color
            }}
          >
            {c.status}
          </span>
        </div>
      ))}
      <div
        style={{
          marginTop: "14px",
          padding: "14px",
          borderRadius: "10px",
          background: d.greenBg,
          textAlign: "center"
        }}
      >
        <div style={{ fontSize: "24px", fontWeight: 700, color: d.greenText }}>
          20-30%
        </div>
        <div style={{ fontSize: "12px", color: d.greenText }}>
          Workforce utilisation improvement
        </div>
      </div>
    </div>
  );

  const invoiceMock = (
    <div
      style={{ ...card(), padding: "32px", boxShadow: d.sh8, direction: "ltr" }}
    >
      <div
        style={{
          fontSize: "11px",
          color: d.textMuted,
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.1em",
          marginBottom: "18px"
        }}
      >
        Revenue Overview
      </div>
      <div style={colsStyle(2, 2)}>
        {[
          { l: "This Month", v: "USD 48,200", c: "↑ 18%", pos: true },
          { l: "Outstanding", v: "USD 3,400", c: "↓ 12%", pos: false },
          { l: "Invoices Sent", v: "124", c: "↑ 34%", pos: true },
          { l: "Paid On Time", v: "96%", c: "↑ 8%", pos: true }
        ].map((r, i) => (
          <div
            key={i}
            style={{
              background: d.bgAlt,
              padding: "16px",
              borderRadius: "12px",
              border: `1px solid ${d.border}`
            }}
          >
            <div
              style={{
                fontSize: "11px",
                color: d.textMuted,
                marginBottom: "5px"
              }}
            >
              {r.l}
            </div>
            <div style={{ fontSize: "18px", fontWeight: 700, color: d.text }}>
              {r.v}
            </div>
            <div
              style={{
                fontSize: "11px",
                fontWeight: 500,
                color: r.pos ? d.greenText : "#D32F2F",
                marginTop: "3px"
              }}
            >
              {r.c}
            </div>
          </div>
        ))}
      </div>
      <div style={{ marginTop: "20px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: "6px"
          }}
        >
          <span style={{ fontSize: "12px", color: d.textMuted }}>
            Collection rate
          </span>
          <span style={{ fontSize: "12px", fontWeight: 600, color: d.primary }}>
            78%
          </span>
        </div>
        <div
          style={{
            height: "6px",
            borderRadius: "3px",
            background: d.border,
            overflow: "hidden"
          }}
        >
          <div
            style={{
              height: "100%",
              width: "78%",
              background: `linear-gradient(90deg,${d.primary},${d.primaryLight})`,
              borderRadius: "3px"
            }}
          />
        </div>
      </div>
    </div>
  );

  const bookingMock = (
    <div
      style={{ ...card(), padding: "36px", boxShadow: d.sh8, direction: "ltr" }}
    >
      <div
        style={{ textAlign: "center", fontSize: "56px", marginBottom: "16px" }}
      >
        📱
      </div>
      <div
        style={{
          fontSize: "18px",
          fontWeight: 700,
          color: d.text,
          textAlign: "center",
          marginBottom: "8px"
        }}
      >
        Book from Anywhere
      </div>
      <p
        style={{
          color: d.textSec,
          fontSize: "14px",
          textAlign: "center",
          marginBottom: "24px"
        }}
      >
        Web · App · Google · WhatsApp · Walk-in
      </p>
      <div style={colsStyle(2, 2)}>
        {["Web Portal", "Mobile App", "Google Maps", "WhatsApp"].map((c, i) => (
          <div
            key={i}
            style={{
              padding: "14px",
              borderRadius: "10px",
              background: d.bgAlt,
              border: `1px solid ${d.border}`,
              fontSize: "13px",
              fontWeight: 500,
              color: d.textSec,
              textAlign: "center"
            }}
          >
            {c}
          </div>
        ))}
      </div>
      <div
        style={{
          marginTop: "20px",
          padding: "14px",
          borderRadius: "10px",
          background: d.primaryCont,
          textAlign: "center"
        }}
      >
        <div style={{ fontSize: "28px", fontWeight: 700, color: d.primary }}>
          +25%
        </div>
        <div style={{ fontSize: "12px", color: d.textSec }}>
          Booking Conversion Uplift
        </div>
      </div>
    </div>
  );

  // Shown in the hero until the dealership screenshot is available.
  const metricsCard = (
    <div style={{ ...card(), padding: "28px", boxShadow: d.sh8 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px"
        }}
      >
        <span
          style={{
            fontSize: "11px",
            color: d.textMuted,
            textTransform: "uppercase",
            letterSpacing: "0.1em",
            fontWeight: 600
          }}
        >
          Live Performance
        </span>
        <div style={{ display: "flex", gap: "5px" }}>
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              style={{
                width: i === activeMetric ? "22px" : "7px",
                height: "7px",
                borderRadius: "4px",
                background: i === activeMetric ? d.primary : d.border,
                transition: "all 0.3s"
              }}
            />
          ))}
        </div>
      </div>

      {metrics.map((m, idx) => (
        <div
          key={idx}
          style={{
            padding: "14px",
            borderRadius: "12px",
            background: idx === activeMetric ? d.primaryCont : "transparent",
            marginBottom: "6px",
            transition: "all 0.4s ease",
            opacity: idx === activeMetric ? 1 : 0.4,
            transform: idx === activeMetric ? "scale(1.02)" : "scale(1)"
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-end",
              marginBottom: "8px"
            }}
          >
            <div>
              <div
                style={{
                  fontSize: "12px",
                  color: d.textSec,
                  marginBottom: "3px"
                }}
              >
                {m.label}
              </div>
              <div
                style={{
                  fontSize: "38px",
                  fontWeight: 700,
                  color: d.primary,
                  lineHeight: 1
                }}
              >
                {m.value}
              </div>
            </div>
            <span
              style={{
                fontSize: "11px",
                color: d.greenText,
                background: d.greenBg,
                padding: "4px 10px",
                borderRadius: "10px",
                fontWeight: 500
              }}
            >
              {m.sub}
            </span>
          </div>
          <div
            style={{
              height: "3px",
              background: d.border,
              borderRadius: "2px",
              overflow: "hidden"
            }}
          >
            <div
              style={{
                height: "100%",
                width: idx === activeMetric ? "80%" : "0%",
                background: d.primary,
                borderRadius: "2px",
                transition: "width 1s ease"
              }}
            />
          </div>
        </div>
      ))}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "10px",
          marginTop: "18px",
          paddingTop: "18px",
          borderTop: `1px solid ${d.border}`
        }}
      >
        {[
          { l: "Active Businesses", v: "50+", c: "↑ Growing fast" },
          { l: "Bookings/Month", v: "50K+", c: "↑ 32% this quarter" }
        ].map((s, i) => (
          <div
            key={i}
            style={{
              background: d.bgAlt,
              padding: "14px",
              borderRadius: "10px"
            }}
          >
            <div
              style={{
                fontSize: "11px",
                color: d.textMuted,
                marginBottom: "4px"
              }}
            >
              {s.l}
            </div>
            <div style={{ fontSize: "22px", fontWeight: 700, color: d.text }}>
              {s.v}
            </div>
            <div style={{ fontSize: "11px", color: d.greenText }}>{s.c}</div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <>
      {/* <Helmet>
  <title>
    Ezeitpro - Manage Booking, Crew, Invoicing & Analytics in One Platform
  </title>

  <meta
    name="description"
    content="EzeitPro is trusted by service businesses to schedule staff, manage crew availability & streamline bookings. Proven results."
  />

  <link
    rel="canonical"
    href="https://www.ezeitpro.com/"
  />

  <meta
    name="robots"
    content="index, follow"
  />

  <script type="text/javascript">
    {`
      _linkedin_partner_id = "9325684";
      window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
      window._linkedin_data_partner_ids.push(_linkedin_partner_id);
    `}
  </script>

  <script type="text/javascript">
    {`
      (function(l) {
        if (!l){
          window.lintrk = function(a,b){
            window.lintrk.q.push([a,b])
          };
          window.lintrk.q=[]
        }

        var s = document.getElementsByTagName("script")[0];
        var b = document.createElement("script");

        b.type = "text/javascript";
        b.async = true;
        b.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";

        s.parentNode.insertBefore(b, s);
      })(window.lintrk);
    `}
  </script>

  <noscript>
    {`
      <img
        height="1"
        width="1"
        style="display:none;"
        alt=""
        src="https://px.ads.linkedin.com/collect/?pid=9325684&fmt=gif"
      />
    `}
  </noscript>
</Helmet> */}
      <Helmet>
        {/* Meta Title */}
        <title>
          EzeitPro - Bookings, Dealership DMS, Finance, Inventory &amp; 24x7 AI
          Assistant
        </title>

        {/* Meta Description */}
        <meta
          name="description"
          content="EzeitPro runs service businesses and car dealerships: bookings, crew, invoicing, dealership management, finance with UAE VAT, inventory with PO and GRN, and a 24x7 WhatsApp AI assistant."
        />

        {/* Open Graph / Twitter */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="EzeitPro" />
        <meta
          property="og:title"
          content="EzeitPro - Bookings, Dealership DMS, Finance, Inventory & 24x7 AI Assistant"
        />
        <meta
          property="og:description"
          content="Run bookings, the workshop, the showroom, the stores and the books on one platform, with an AI assistant that answers your customers 24x7."
        />
        <meta property="og:url" content="https://www.ezeitpro.com/" />
        <meta
          property="og:image"
          content="https://www.ezeitpro.com/image/website/og-home.png"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            name: "EzeitPro",
            applicationCategory: "BusinessApplication",
            operatingSystem: "Web",
            url: "https://www.ezeitpro.com/",
            description:
              "Booking, crew, invoicing, dealership management, finance, inventory and a 24x7 WhatsApp AI assistant for service businesses and car dealerships.",
            publisher: {
              "@type": "Organization",
              name: "UtilStop LLC-FZ",
              url: "https://www.ezeitpro.com/"
            }
          })}
        </script>

        {/* Canonical URL */}
        <link rel="canonical" href="https://www.ezeitpro.com/" />

        {/* Robots */}
        <meta name="robots" content="index, follow" />

        {/* Google Tag Manager */}
        <script type="text/javascript">
          {`
      (function(w,d,s,l,i){
        w[l]=w[l]||[];
        w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});
        var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),
        dl=l!='dataLayer'?'&l='+l:'';

        j.async=true;
        j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;

        f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','GTM-ML4SQF87');
    `}
        </script>

        {/* LinkedIn Insight Tag */}
        <script type="text/javascript">
          {`
      _linkedin_partner_id = "9325684";
      window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
      window._linkedin_data_partner_ids.push(_linkedin_partner_id);
    `}
        </script>

        <script type="text/javascript">
          {`
      (function(l) {
        if (!l){
          window.lintrk = function(a,b){
            window.lintrk.q.push([a,b])
          };
          window.lintrk.q=[]
        }

        var s = document.getElementsByTagName("script")[0];
        var b = document.createElement("script");

        b.type = "text/javascript";
        b.async = true;
        b.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";

        s.parentNode.insertBefore(b, s);
      })(window.lintrk);
    `}
        </script>
      </Helmet>
      <div
        data-site-theme={isDark ? "dark" : "light"}
        style={{
          background: d.bg,
          color: d.text,
          minHeight: "100vh",
          fontFamily: "'Roboto','Segoe UI',Arial,sans-serif",
          transition: "background 0.3s,color 0.3s",
          overflowX: "hidden"
        }}
      >
        {/* ── HEADER ── */}
        <header
          style={{
            position: "fixed",
            top: 0,
            width: "100%",
            zIndex: 1000,
            background: scrolled ? d.navBg : "transparent",
            backdropFilter: scrolled ? "blur(20px)" : "none",
            borderBottom: scrolled ? `1px solid ${d.border}` : "none",
            boxShadow: scrolled ? d.sh2 : "none",
            transition: "all 0.3s ease",
            boxSizing: "border-box"
          }}
        >
          <nav
            style={{
              maxWidth: "1280px",
              margin: "0 auto",
              padding: "0 24px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              height: "68px"
            }}
          >
            <BrandLogo height={38} tone={isDark ? "dark" : "light"} />

            {!isMobile && (
              <div
                style={{ display: "flex", gap: "32px", alignItems: "center" }}
              >
                <a
                  href="#features"
                  style={{
                    color: d.text,
                    textDecoration: "none",
                    fontSize: "15px",
                    fontWeight: 700,
                    letterSpacing: "-0.01em"
                  }}
                >
                  Features
                </a>
                <a
                  href="#modules"
                  style={{
                    color: d.text,
                    textDecoration: "none",
                    fontSize: "15px",
                    fontWeight: 700,
                    letterSpacing: "-0.01em"
                  }}
                >
                  Dealership &amp; AI
                </a>

                {/* Use Cases with mega-menu */}
                <div
                  style={{ position: "relative" }}
                  onMouseEnter={openMega}
                  onMouseLeave={closeMega}
                >
                  <button
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                      background: "none",
                      border: "none",
                      color: d.text,
                      fontSize: "15px",
                      fontWeight: 700,
                      letterSpacing: "-0.01em",
                      cursor: "pointer",
                      padding: 0
                    }}
                  >
                    Use Cases{" "}
                    <ChevronDown
                      size={14}
                      style={{
                        transition: "transform 0.2s",
                        transform: megaMenuOpen
                          ? "rotate(180deg)"
                          : "rotate(0deg)"
                      }}
                    />
                  </button>

                  {megaMenuOpen && (
                    <div
                      onMouseEnter={openMega}
                      onMouseLeave={closeMega}
                      style={{
                        position: "fixed",
                        top: "68px",
                        left: 0,
                        right: 0,
                        background: scrolled ? d.navBg : d.surface,
                        backdropFilter: "blur(20px)",
                        borderBottom: `1px solid ${d.border}`,
                        boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
                        zIndex: 999,
                        padding: "28px 24px 32px"
                      }}
                    >
                      <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
                        <div
                          style={{
                            fontSize: "13px",
                            fontWeight: 700,
                            color: d.textMuted,
                            textTransform: "uppercase",
                            letterSpacing: "0.08em",
                            marginBottom: "20px"
                          }}
                        >
                          Industries We Serve
                        </div>
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(3, 1fr)",
                            gap: "0"
                          }}
                        >
                          {USE_CASES.map((uc, idx) => (
                            <div
                              key={uc.slug}
                              style={{
                                padding: "10px 16px",
                                borderLeft:
                                  idx % 3 !== 0
                                    ? `1px solid ${d.border}`
                                    : "none"
                              }}
                            >
                              <Link
                                to={`/use-cases/${uc.slug}`}
                                onClick={() => setMegaMenuOpen(false)}
                                style={{
                                  display: "block",
                                  fontSize: "15px",
                                  fontWeight: 700,
                                  color: d.primary,
                                  textDecoration: "none",
                                  marginBottom: "8px"
                                }}
                              >
                                {uc.industry}
                              </Link>
                              {uc.services.map((svc) => (
                                <Link
                                  key={svc}
                                  to={`/use-cases/${uc.slug}`}
                                  onClick={() => setMegaMenuOpen(false)}
                                  style={{
                                    display: "block",
                                    fontSize: "14px",
                                    color: d.textSec,
                                    textDecoration: "none",
                                    lineHeight: "1.9",
                                    paddingLeft: "8px"
                                  }}
                                  onMouseEnter={(e) =>
                                    (e.currentTarget.style.color = d.primary)
                                  }
                                  onMouseLeave={(e) =>
                                    (e.currentTarget.style.color = d.textSec)
                                  }
                                >
                                  · {svc}
                                </Link>
                              ))}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <a
                  href="#pricing"
                  style={{
                    color: d.text,
                    textDecoration: "none",
                    fontSize: "15px",
                    fontWeight: 700,
                    letterSpacing: "-0.01em"
                  }}
                >
                  Pricing
                </a>
              </div>
            )}

            {!isMobile && (
              <div
                style={{ display: "flex", gap: "10px", alignItems: "center" }}
              >
                <ThemeToggle
                  isDark={isDark}
                  onToggle={toggleTheme}
                  color={d.text}
                  border={d.borderMd}
                />
                <button style={btnO} onClick={() => navigate("/login")}>
                  Login
                </button>
                <button
                  style={btnP}
                  onClick={() =>
                    document
                      .getElementById("contact")
                      ?.scrollIntoView({ behavior: "smooth" })
                  }
                >
                  Request Demo <ArrowRight size={15} />
                </button>
              </div>
            )}

            {isMobile && (
              <div
                style={{ display: "flex", alignItems: "center", gap: "10px" }}
              >
                <ThemeToggle
                  isDark={isDark}
                  onToggle={toggleTheme}
                  color={d.text}
                  border={d.borderMd}
                  size={36}
                />
                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
                  style={{
                    background: "none",
                    border: "none",
                    color: d.text,
                    cursor: "pointer",
                    padding: "4px"
                  }}
                >
                  {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                </button>
              </div>
            )}
          </nav>

          {isMobile && mobileMenuOpen && (
            <div
              style={{
                background: d.surface,
                borderTop: `1px solid ${d.border}`,
                padding: "20px 24px",
                display: "flex",
                flexDirection: "column",
                gap: "14px"
              }}
            >
              <a
                href="#features"
                onClick={() => setMobileMenuOpen(false)}
                style={{
                  color: d.text,
                  textDecoration: "none",
                  fontSize: "15px",
                  fontWeight: 700
                }}
              >
                Features
              </a>
              <a
                href="#modules"
                onClick={() => setMobileMenuOpen(false)}
                style={{
                  color: d.text,
                  textDecoration: "none",
                  fontSize: "15px",
                  fontWeight: 700
                }}
              >
                Dealership &amp; AI
              </a>

              {/* Use Cases accordion */}
              <button
                onClick={() => setMobileUseCasesOpen((v) => !v)}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  background: "none",
                  border: "none",
                  padding: 0,
                  color: d.text,
                  fontSize: "15px",
                  fontWeight: 700,
                  cursor: "pointer",
                  textAlign: "left"
                }}
              >
                Use Cases
                <ChevronDown
                  size={16}
                  style={{
                    transform: mobileUseCasesOpen
                      ? "rotate(180deg)"
                      : "rotate(0deg)",
                    transition: "transform 0.2s"
                  }}
                />
              </button>
              {mobileUseCasesOpen && (
                <div
                  style={{
                    paddingLeft: "12px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                    borderLeft: `2px solid ${d.border}`
                  }}
                >
                  {USE_CASES.map((uc) => (
                    <Link
                      key={uc.slug}
                      to={`/use-cases/${uc.slug}`}
                      onClick={() => {
                        setMobileMenuOpen(false);
                        setMobileUseCasesOpen(false);
                      }}
                      style={{
                        color: d.textSec,
                        textDecoration: "none",
                        fontSize: "14px",
                        padding: "4px 0",
                        fontWeight: 500
                      }}
                    >
                      {uc.industry}
                    </Link>
                  ))}
                </div>
              )}

              <a
                href="#pricing"
                onClick={() => setMobileMenuOpen(false)}
                style={{
                  color: d.text,
                  textDecoration: "none",
                  fontSize: "15px",
                  fontWeight: 700
                }}
              >
                Pricing
              </a>
              <button
                style={{ ...btnP, justifyContent: "center" }}
                onClick={() => navigate("/login")}
              >
                Login
              </button>
            </div>
          )}
        </header>

        {/* ── HERO ── */}
        <section
          style={{
            background: d.heroBg,
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            position: "relative",
            overflow: "hidden",
            paddingTop: "68px"
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              backgroundImage: `linear-gradient(${isDark ? "rgba(255,255,255,0.025)" : "rgba(0,0,0,0.025)"} 1px,transparent 1px),linear-gradient(90deg,${isDark ? "rgba(255,255,255,0.025)" : "rgba(0,0,0,0.025)"} 1px,transparent 1px)`,
              backgroundSize: "56px 56px"
            }}
          />

          <div
            style={{
              maxWidth: "1280px",
              margin: "0 auto",
              padding: isMobile ? "60px 24px" : "80px 24px",
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              gap: "56px",
              alignItems: "center",
              position: "relative",
              zIndex: 1,
              width: "100%",
              boxSizing: "border-box"
            }}
          >
            <div>
              <div style={pill}>All-in-One Platform for Service Businesses</div>
              <h1
                style={{
                  fontSize: isMobile ? "40px" : "clamp(40px,5vw,68px)",
                  fontWeight: 700,
                  lineHeight: 1.1,
                  marginBottom: "20px",
                  color: d.text
                }}
              >
                <span style={{ color: d.primary }}>Simplify</span> Operations.
                <br />
                Delight Customers.
                <br />
                <span style={{ color: d.primary }}>Grow</span> Faster.
              </h1>
              <p
                style={{
                  fontSize: "17px",
                  color: d.textSec,
                  lineHeight: 1.75,
                  marginBottom: "14px",
                  maxWidth: "500px"
                }}
              >
                EzeitPro — the operating backbone for modern service businesses,
                from the first booking to the ledger.
              </p>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "8px",
                  marginBottom: "36px"
                }}
              >
                {[
                  "Scheduling",
                  "Booking",
                  "Crew Management",
                  "Invoicing",
                  "Dealership DMS",
                  "Finance",
                  "Inventory & GRN",
                  "AI Assistant 24x7",
                  "HRMS",
                  "Analytics"
                ].map((tag) => (
                  <span
                    key={tag}
                    style={{
                      background: d.chipBg,
                      color: d.chipText,
                      padding: "4px 12px",
                      borderRadius: "10px",
                      fontSize: "12px",
                      fontWeight: 500
                    }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                <button
                  style={btnP}
                  onClick={() =>
                    document
                      .getElementById("contact")
                      ?.scrollIntoView({ behavior: "smooth" })
                  }
                >
                  Book a Demo <ArrowRight size={15} />
                </button>
                <button style={btnO} onClick={() => setShowVideoModal(true)}>
                  <Play size={14} /> See How It Works
                </button>
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "16px",
                  marginTop: "36px",
                  padding: "16px 20px",
                  borderRadius: "14px",
                  background: isDark
                    ? "rgba(255,255,255,0.05)"
                    : "rgba(255,255,255,0.7)",
                  backdropFilter: "blur(8px)",
                  border: `1px solid ${d.border}`,
                  width: "fit-content"
                }}
              >
                <div style={{ display: "flex" }}>
                  {[...Array(4)].map((_, i) => (
                    <div
                      key={i}
                      style={{
                        width: "36px",
                        height: "36px",
                        borderRadius: "50%",
                        background: `hsl(${i * 60 + 340},60%,70%)`,
                        border: `2px solid ${d.surface}`,
                        marginLeft: i > 0 ? "-10px" : "0"
                      }}
                    />
                  ))}
                </div>
                <div>
                  <div
                    style={{ fontWeight: 600, fontSize: "14px", color: d.text }}
                  >
                    50+ Service Businesses
                  </div>
                  <div style={{ fontSize: "12px", color: d.textMuted }}>
                    Trust EzeitPro daily
                  </div>
                </div>
              </div>
            </div>

            {/* Product screenshot (falls back to the live-performance card until the image exists) */}
            <DeviceCollage
              d={d}
              {...HERO_SHOT}
              eager
              fallback={metricsCard}
              overlay={
                <div
                  style={{
                    position: "absolute",
                    right: isMobile ? 4 : -14,
                    top: isMobile ? -14 : -26,
                    width: isMobile ? 220 : 260,
                    zIndex: 3,
                    ...card(),
                    padding: "12px 14px",
                    boxShadow: d.sh8
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginBottom: 6
                    }}
                  >
                    <div
                      style={{
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        background: d.primaryCont,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                      }}
                    >
                      <Bot size={14} color={d.primary} />
                    </div>
                    <span
                      style={{ fontSize: 12, fontWeight: 700, color: d.text }}
                    >
                      AI Assistant
                    </span>
                    <span
                      style={{
                        marginLeft: "auto",
                        fontSize: 10,
                        color: d.greenText,
                        background: d.greenBg,
                        padding: "2px 8px",
                        borderRadius: 10,
                        fontWeight: 600
                      }}
                    >
                      24x7
                    </span>
                  </div>
                  <div
                    style={{ fontSize: 12, color: d.textSec, lineHeight: 1.5 }}
                  >
                    Full service from AED 420. Open slots tomorrow at 10:00 and
                    14:30.
                  </div>
                </div>
              }
            />
          </div>
        </section>

        {/* ── STATS BAR ── */}
        <section
          style={{
            background: d.surface,
            borderTop: `1px solid ${d.border}`,
            borderBottom: `1px solid ${d.border}`,
            padding: "48px 24px",
            boxShadow: d.sh1
          }}
        >
          <div
            style={{ maxWidth: "1280px", margin: "0 auto", ...colsStyle(4, 2) }}
          >
            {[
              { value: "50+", label: "Service Businesses", icon: Users },
              { value: "25%", label: "More Bookings", icon: TrendingUp },
              { value: "30%", label: "Fewer No-Shows", icon: CheckCircle },
              { value: "20-30%", label: "Better Utilisation", icon: BarChart3 }
            ].map((s, i) => (
              <div
                key={i}
                style={{
                  textAlign: "center",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "8px"
                }}
              >
                <div
                  style={{
                    width: "48px",
                    height: "48px",
                    borderRadius: "14px",
                    background: d.primaryCont,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}
                >
                  <s.icon size={24} color={d.primary} />
                </div>
                <div
                  style={{
                    fontSize: "34px",
                    fontWeight: 700,
                    color: d.primary
                  }}
                >
                  {s.value}
                </div>
                <div style={{ fontSize: "13px", color: d.textMuted }}>
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── PROBLEMS ── */}
        <section style={{ padding: "88px 24px", background: d.bg }}>
          <div style={{ maxWidth: "1280px", margin: "0 auto" }}>
            <div style={{ textAlign: "center", marginBottom: "56px" }}>
              <div style={pill}>The Challenge</div>
              <h2
                style={{
                  fontSize: isMobile ? "32px" : "clamp(32px,4vw,52px)",
                  fontWeight: 700,
                  lineHeight: 1.2,
                  marginBottom: "14px"
                }}
              >
                Running a Service Business
                <br />
                Shouldn't Be This Hard
              </h2>
              <p
                style={{
                  fontSize: "17px",
                  color: d.textSec,
                  maxWidth: "520px",
                  margin: "0 auto",
                  lineHeight: 1.7
                }}
              >
                Traditional processes are costing you revenue, customers, and
                growth.
              </p>
            </div>
            <div style={colsStyle(3)}>
              {problems.map((p, i) => (
                <div
                  key={i}
                  style={card()}
                  onMouseEnter={(e) => hoverCard(e, true)}
                  onMouseLeave={(e) => hoverCard(e, false)}
                >
                  <div style={{ padding: "28px" }}>
                    <div
                      style={{
                        width: "52px",
                        height: "52px",
                        borderRadius: "14px",
                        background: d.primaryCont,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginBottom: "18px"
                      }}
                    >
                      <p.icon size={26} color={d.primary} />
                    </div>
                    <h3
                      style={{
                        fontSize: "17px",
                        fontWeight: 700,
                        marginBottom: "8px",
                        color: d.text
                      }}
                    >
                      {p.title}
                    </h3>
                    <p
                      style={{
                        fontSize: "14px",
                        color: d.textSec,
                        lineHeight: 1.7,
                        marginBottom: "20px"
                      }}
                    >
                      {p.desc}
                    </p>
                    <div
                      style={{
                        borderTop: `1px solid ${d.border}`,
                        paddingTop: "16px",
                        display: "flex",
                        alignItems: "flex-end",
                        gap: "8px"
                      }}
                    >
                      <span
                        style={{
                          fontSize: "36px",
                          fontWeight: 700,
                          color: "#D32F2F",
                          lineHeight: 1
                        }}
                      >
                        {p.stat}
                      </span>
                      <span
                        style={{
                          fontSize: "12px",
                          color: d.textMuted,
                          marginBottom: "4px"
                        }}
                      >
                        {p.statLabel}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── EMPOWER QUOTE ── */}
        <section style={{ padding: "64px 24px", background: d.primaryCont }}>
          <div
            style={{ maxWidth: "800px", margin: "0 auto", textAlign: "center" }}
          >
            <div
              style={{
                fontSize: isMobile ? "22px" : "28px",
                fontWeight: 700,
                color: d.text,
                marginBottom: "16px",
                lineHeight: 1.4
              }}
            >
              Empowering SMBs with Enterprise-Grade Technology
            </div>
            <p
              style={{
                fontSize: "16px",
                color: d.textSec,
                lineHeight: 1.75,
                marginBottom: "24px"
              }}
            >
              Small and medium service businesses deserve the same powerful
              tools as large enterprises — without the complexity or prohibitive
              costs. EzeitPro brings automation, simplicity, and scalability to
              your everyday operations.
            </p>
            <blockquote
              style={{
                borderLeft: `4px solid ${d.primary}`,
                paddingLeft: "20px",
                textAlign: "left",
                display: "inline-block"
              }}
            >
              <p
                style={{
                  fontSize: "17px",
                  fontStyle: "italic",
                  color: d.primary,
                  fontWeight: 500
                }}
              >
                "EzeitPro is the operating backbone for modern service
                businesses."
              </p>
            </blockquote>
          </div>
        </section>

        {/* ── FEATURES GRID ── */}
        <section
          id="features"
          style={{ padding: "88px 24px", background: d.bgAlt }}
        >
          <div style={{ maxWidth: "1280px", margin: "0 auto" }}>
            <div style={{ textAlign: "center", marginBottom: "56px" }}>
              <div style={pill}>Platform Features</div>
              <h2
                style={{
                  fontSize: isMobile ? "32px" : "clamp(32px,4vw,52px)",
                  fontWeight: 700,
                  marginBottom: "14px"
                }}
              >
                One Platform to Run
                <br />
                Your Entire Business
              </h2>
              <p
                style={{
                  fontSize: "17px",
                  color: d.textSec,
                  maxWidth: "480px",
                  margin: "0 auto",
                  lineHeight: 1.7
                }}
              >
                Everything you need to manage, grow, and delight — in one place.
              </p>
            </div>
            <div style={colsStyle(3, 1)}>
              {features.map((f, i) => (
                <div
                  key={i}
                  style={card({ cursor: "pointer" })}
                  onMouseEnter={(e) => {
                    hoverCard(e, true);
                    e.currentTarget.style.borderColor = d.primary;
                  }}
                  onMouseLeave={(e) => {
                    hoverCard(e, false);
                    e.currentTarget.style.borderColor = d.border;
                  }}
                >
                  <div style={{ padding: "28px" }}>
                    <div
                      style={{
                        width: "52px",
                        height: "52px",
                        borderRadius: "14px",
                        background: d.primaryCont,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginBottom: "16px"
                      }}
                    >
                      <f.icon size={26} color={d.primary} />
                    </div>
                    <h3
                      style={{
                        fontSize: "16px",
                        fontWeight: 700,
                        marginBottom: "8px",
                        color: d.text
                      }}
                    >
                      {f.title}
                    </h3>
                    {f.enterprise && (
                      <div style={{ marginBottom: "8px" }}>
                        <EnterpriseBadge d={d} />
                      </div>
                    )}
                    <p
                      style={{
                        fontSize: "13px",
                        color: d.textSec,
                        lineHeight: 1.65
                      }}
                    >
                      {f.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── DEALERSHIP, FINANCE, INVENTORY, AI ── */}
        <section
          id="modules"
          style={{ padding: "88px 24px", background: d.bg }}
        >
          <div style={{ maxWidth: "1280px", margin: "0 auto" }}>
            <div
              style={{
                textAlign: "center",
                marginBottom: isMobile ? "48px" : "72px"
              }}
            >
              <div style={pill}>New: Built for the Whole Business</div>
              <h2
                style={{
                  fontSize: isMobile ? "32px" : "clamp(32px,4vw,52px)",
                  fontWeight: 700,
                  lineHeight: 1.2,
                  marginBottom: "14px"
                }}
              >
                Dealership, Finance, Inventory
                <br />
                and a 24x7 AI Assistant
              </h2>
              <p
                style={{
                  fontSize: "17px",
                  color: d.textSec,
                  maxWidth: "620px",
                  margin: "0 auto",
                  lineHeight: 1.7
                }}
              >
                Go beyond bookings. Run vehicle sales, the workshop, the stores
                and the books on the same platform, with an assistant that
                answers your customers while you sleep.
              </p>
            </div>
            <ModuleShowcase
              d={d}
              isDark={isDark}
              isMobile={isMobile}
              modules={MODULES}
              onContact={goToContact}
              ctaLabel="Contact us about this module"
            />
            <p
              style={{
                textAlign: "center",
                marginTop: "56px",
                fontSize: "13px",
                color: d.textMuted
              }}
            >
              Available on the Enterprise plan. Screens shown use sample data.
            </p>
          </div>
        </section>

        {/* ── MULTI-CHANNEL BOOKING ── */}
        <section style={{ padding: "88px 24px", background: d.bg }}>
          <div style={{ maxWidth: "1280px", margin: "0 auto" }}>
            <div style={twoCol()}>
              <div style={{ direction: "ltr" }}>
                <div style={pill}>Never Miss a Customer Again</div>
                <h2
                  style={{
                    fontSize: isMobile ? "28px" : "clamp(28px,3.5vw,44px)",
                    fontWeight: 700,
                    marginBottom: "14px",
                    direction: "ltr"
                  }}
                >
                  Multi-Channel Booking
                  <br />
                  Made Simple
                </h2>
                <p
                  style={{
                    fontSize: "16px",
                    color: d.textSec,
                    lineHeight: 1.75,
                    marginBottom: "28px"
                  }}
                >
                  Meet customers wherever they are — web, app, Google, phone, or
                  walk-in.
                </p>
                {bookingChannels.map((c, i) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      gap: "14px",
                      marginBottom: "12px",
                      padding: "14px 16px",
                      borderRadius: "12px",
                      background: d.bgAlt,
                      border: `1px solid ${d.border}`
                    }}
                  >
                    <div
                      style={{
                        width: "34px",
                        height: "34px",
                        borderRadius: "10px",
                        background: d.primaryCont,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0
                      }}
                    >
                      <ChevronRight size={16} color={d.primary} />
                    </div>
                    <div>
                      <div
                        style={{
                          fontSize: "14px",
                          fontWeight: 600,
                          marginBottom: "3px",
                          color: d.text
                        }}
                      >
                        {c.title}
                      </div>
                      <div style={{ fontSize: "12px", color: d.textSec }}>
                        {c.desc}
                      </div>
                    </div>
                  </div>
                ))}
                <div
                  style={{
                    marginTop: "20px",
                    padding: "14px 18px",
                    borderRadius: "12px",
                    background: d.greenBg,
                    border: `1px solid ${isDark ? "rgba(76,175,80,0.3)" : "#C8E6C9"}`,
                    display: "flex",
                    alignItems: "center",
                    gap: "10px"
                  }}
                >
                  <CheckCircle size={18} color={d.greenText} />
                  <span
                    style={{
                      fontSize: "13px",
                      color: d.greenText,
                      fontWeight: 500
                    }}
                  >
                    Up to <strong>25% increase</strong> in booking conversions
                    with multi-channel access
                  </span>
                </div>
              </div>
              <div style={{ direction: "ltr" }}>
                <DeviceCollage
                  d={d}
                  {...CORE_SHOTS.dashboard}
                  fallback={bookingMock}
                />
              </div>
            </div>
          </div>
        </section>

        {/* ── SMS & WHATSAPP ── */}
        <section style={{ padding: "88px 24px", background: d.bgAlt }}>
          <div style={{ maxWidth: "1280px", margin: "0 auto" }}>
            <div style={{ textAlign: "center", marginBottom: "48px" }}>
              <div style={pill}>Automated Communications</div>
              <h2
                style={{
                  fontSize: isMobile ? "28px" : "clamp(28px,3.5vw,44px)",
                  fontWeight: 700,
                  marginBottom: "14px"
                }}
              >
                SMS & WhatsApp — Always On
              </h2>
              <p
                style={{
                  fontSize: "16px",
                  color: d.textSec,
                  maxWidth: "560px",
                  margin: "0 auto",
                  lineHeight: 1.75
                }}
              >
                From first booking to final feedback — stay connected at every
                step of the customer journey without lifting a finger.
              </p>
            </div>
            <div style={colsStyle(3)}>
              {[
                {
                  icon: CheckCircle,
                  title: "Instant Confirmations",
                  desc: "Automatic booking confirmation the moment a customer books"
                },
                {
                  icon: Clock,
                  title: "Smart Reminders",
                  desc: "Multi-stage reminders that cut no-shows by up to 30%"
                },
                {
                  icon: MessageSquare,
                  title: "Post-Service Follow-ups",
                  desc: "Automated review requests and loyalty messages after each job"
                }
              ].map((s, i) => (
                <div
                  key={i}
                  style={card()}
                  onMouseEnter={(e) => hoverCard(e, true)}
                  onMouseLeave={(e) => hoverCard(e, false)}
                >
                  <div style={{ padding: "28px" }}>
                    <div
                      style={{
                        width: "52px",
                        height: "52px",
                        borderRadius: "14px",
                        background: d.primaryCont,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginBottom: "16px"
                      }}
                    >
                      <s.icon size={26} color={d.primary} />
                    </div>
                    <h3
                      style={{
                        fontSize: "16px",
                        fontWeight: 700,
                        color: d.text,
                        marginBottom: "8px"
                      }}
                    >
                      {s.title}
                    </h3>
                    <p
                      style={{
                        fontSize: "13px",
                        color: d.textSec,
                        lineHeight: 1.65
                      }}
                    >
                      {s.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <div
              style={{
                textAlign: "center",
                padding: "16px 24px",
                borderRadius: "14px",
                background: d.greenBg,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "10px",
                margin: "32px auto 0"
              }}
            >
              <CheckCircle size={18} color={d.greenText} />
              <span
                style={{
                  fontSize: "14px",
                  color: d.greenText,
                  fontWeight: 500
                }}
              >
                Automated messaging reduces appointment no-shows by up to{" "}
                <strong>30%</strong>
              </span>
            </div>
          </div>
        </section>

        {/* ── CREW MANAGEMENT ── */}
        <section style={{ padding: "88px 24px", background: d.bg }}>
          <div style={{ maxWidth: "1280px", margin: "0 auto" }}>
            <div style={twoCol()}>
              <div style={{ direction: "ltr" }}>
                <div style={pill}>Put the Right People in the Right Place</div>
                <h2
                  style={{
                    fontSize: isMobile ? "28px" : "clamp(28px,3.5vw,44px)",
                    fontWeight: 700,
                    marginBottom: "14px",
                    direction: "ltr"
                  }}
                >
                  Smart Resource &<br />
                  Crew Management
                </h2>
                <p
                  style={{
                    fontSize: "16px",
                    color: d.textSec,
                    lineHeight: 1.75,
                    marginBottom: "28px"
                  }}
                >
                  Businesses report a 20–30% improvement in workforce
                  utilisation after adopting smart scheduling tools.
                </p>
                {[
                  {
                    icon: BarChart3,
                    title: "Visual Scheduling Dashboard",
                    desc: "Intuitive drag-and-drop interface for complete crew visibility"
                  },
                  {
                    icon: Clock,
                    title: "Real-Time Availability",
                    desc: "Track utilisation and prevent over-booking or idle time"
                  },
                  {
                    icon: Zap,
                    title: "Smart Job Assignment",
                    desc: "Automatically match the best-fit crew member to each job"
                  }
                ].map((f, i) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      gap: "14px",
                      marginBottom: "18px"
                    }}
                  >
                    <div
                      style={{
                        width: "44px",
                        height: "44px",
                        borderRadius: "12px",
                        background: d.primaryCont,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0
                      }}
                    >
                      <f.icon size={22} color={d.primary} />
                    </div>
                    <div>
                      <div
                        style={{
                          fontSize: "15px",
                          fontWeight: 600,
                          color: d.text,
                          marginBottom: "3px"
                        }}
                      >
                        {f.title}
                      </div>
                      <div style={{ fontSize: "13px", color: d.textSec }}>
                        {f.desc}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ direction: "ltr" }}>
                <DeviceCollage d={d} {...CORE_SHOTS.crew} fallback={crewMock} />
              </div>
            </div>
          </div>
        </section>

        {/* ── INVOICING ── */}
        <section style={{ padding: "88px 24px", background: d.bgAlt }}>
          <div style={{ maxWidth: "1280px", margin: "0 auto" }}>
            <div style={twoCol()}>
              <div style={{ direction: "ltr" }}>
                <div style={pill}>Get Paid Faster</div>
                <h2
                  style={{
                    fontSize: isMobile ? "28px" : "clamp(28px,3.5vw,44px)",
                    fontWeight: 700,
                    marginBottom: "14px",
                    direction: "ltr"
                  }}
                >
                  Seamless Invoice &<br />
                  Revenue Management
                </h2>
                <p
                  style={{
                    fontSize: "16px",
                    color: d.textSec,
                    lineHeight: 1.75,
                    marginBottom: "28px"
                  }}
                >
                  Fully compliant for UAE (VAT) and India (GST) — automated,
                  accurate, and instant.
                </p>
                <div style={colsStyle(2, 2)}>
                  {[
                    {
                      num: "01",
                      title: "Auto Invoice Generation",
                      desc: "Invoices created instantly upon service completion — zero manual effort"
                    },
                    {
                      num: "02",
                      title: "TAX Compliant",
                      desc: "Fully compliant for UAE (VAT) and India (GST) tax requirements"
                    },
                    {
                      num: "03",
                      title: "Payment Tracking",
                      desc: "Real-time visibility into outstanding balances and revenue flow"
                    },
                    {
                      num: "04",
                      title: "Fewer Billing Errors",
                      desc: "Automated processes reduce costly mistakes and disputes"
                    }
                  ].map((s, i) => (
                    <div key={i} style={{ ...card(), padding: "20px" }}>
                      <div
                        style={{
                          fontSize: "20px",
                          fontWeight: 700,
                          color: d.primary,
                          marginBottom: "8px"
                        }}
                      >
                        {s.num}
                      </div>
                      <div
                        style={{
                          fontSize: "14px",
                          fontWeight: 600,
                          color: d.text,
                          marginBottom: "6px"
                        }}
                      >
                        {s.title}
                      </div>
                      <div
                        style={{
                          fontSize: "12px",
                          color: d.textSec,
                          lineHeight: 1.6
                        }}
                      >
                        {s.desc}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ direction: "ltr" }}>
                <DeviceCollage
                  d={d}
                  {...CORE_SHOTS.invoice}
                  fallback={invoiceMock}
                />
              </div>
            </div>

            <div className="card-stats">
              <div className="stat-box">
                <span>Time Saved</span>
                <h3>85%</h3>
              </div>

              <div className="stat-box">
                <span>Productivity</span>
                <h3>+35%</h3>
              </div>
            </div>
          </div>
        </section>

        {/* ── ANALYTICS ── */}
        <section style={{ padding: "88px 24px", background: d.bg }}>
          <div style={{ maxWidth: "1280px", margin: "0 auto" }}>
            <div style={{ textAlign: "center", marginBottom: "56px" }}>
              <div style={pill}>Turn Data into Growth</div>
              <h2
                style={{
                  fontSize: isMobile ? "32px" : "clamp(32px,4vw,52px)",
                  fontWeight: 700,
                  marginBottom: "14px"
                }}
              >
                Advanced Analytics for
                <br />
                Smarter Decisions
              </h2>
              <p
                style={{
                  fontSize: "17px",
                  color: d.textSec,
                  maxWidth: "520px",
                  margin: "0 auto",
                  lineHeight: 1.7
                }}
              >
                EzeitPro surfaces the insights that matter most — spend less
                time guessing and more time growing.
              </p>
            </div>
            <div style={colsStyle(3)}>
              {[
                {
                  icon: TrendingUp,
                  title: "Revenue & Booking Trends",
                  desc: "Identify peak periods and revenue patterns at a glance",
                  stat: "↑ 18%",
                  statLabel: "Revenue Growth"
                },
                {
                  icon: Users,
                  title: "Staff & Service Performance",
                  desc: "See who and what is driving results across your business",
                  stat: "94%",
                  statLabel: "Team Utilisation"
                },
                {
                  icon: Shield,
                  title: "Customer Retention Insights",
                  desc: "Understand behaviour and act before customers churn",
                  stat: "↓ 23%",
                  statLabel: "Churn Rate"
                }
              ].map((a, i) => (
                <div
                  key={i}
                  style={card()}
                  onMouseEnter={(e) => hoverCard(e, true)}
                  onMouseLeave={(e) => hoverCard(e, false)}
                >
                  <div style={{ padding: "28px" }}>
                    <div
                      style={{
                        width: "52px",
                        height: "52px",
                        borderRadius: "14px",
                        background: d.primaryCont,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginBottom: "18px"
                      }}
                    >
                      <a.icon size={26} color={d.primary} />
                    </div>
                    <h3
                      style={{
                        fontSize: "17px",
                        fontWeight: 700,
                        marginBottom: "8px",
                        color: d.text
                      }}
                    >
                      {a.title}
                    </h3>
                    <p
                      style={{
                        fontSize: "14px",
                        color: d.textSec,
                        lineHeight: 1.65,
                        marginBottom: "20px"
                      }}
                    >
                      {a.desc}
                    </p>
                    <div
                      style={{
                        borderTop: `1px solid ${d.border}`,
                        paddingTop: "16px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center"
                      }}
                    >
                      <span
                        style={{
                          fontSize: "28px",
                          fontWeight: 700,
                          color: d.primary
                        }}
                      >
                        {a.stat}
                      </span>
                      <span style={{ fontSize: "12px", color: d.textMuted }}>
                        {a.statLabel}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ maxWidth: "860px", margin: "48px auto 0" }}>
              <DeviceCollage d={d} {...CORE_SHOTS.analytics} fallback={<></>} />
            </div>
          </div>
        </section>

        {/* ── ECOSYSTEM ── */}
        <section style={{ padding: "88px 24px", background: d.bgAlt }}>
          <div style={{ maxWidth: "1280px", margin: "0 auto" }}>
            <div style={{ textAlign: "center", marginBottom: "56px" }}>
              <div style={pill}>Complete Ecosystem</div>
              <h2
                style={{
                  fontSize: isMobile ? "28px" : "clamp(28px,3.5vw,44px)",
                  fontWeight: 700,
                  marginBottom: "14px"
                }}
              >
                Demand Meets Delivery:
                <br />
                The EzeitPro Ecosystem
              </h2>
            </div>
            <div style={colsStyle(2)}>
              <div style={card({ padding: "36px", boxShadow: d.sh4 })}>
                <div
                  style={{
                    width: "60px",
                    height: "60px",
                    borderRadius: "18px",
                    background: d.primaryCont,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: "20px"
                  }}
                >
                  <Layers size={30} color={d.primary} />
                </div>
                <h3
                  style={{
                    fontSize: "22px",
                    fontWeight: 700,
                    color: d.text,
                    marginBottom: "8px"
                  }}
                >
                  EzeitPro Core
                </h3>
                <p
                  style={{
                    fontSize: "15px",
                    color: d.textSec,
                    lineHeight: 1.7,
                    marginBottom: "20px"
                  }}
                >
                  Core platform managing operations, scheduling, invoicing, crew
                  management, inventory, expenses, HR and analytics.
                </p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                  {[
                    "Scheduling",
                    "Invoicing",
                    "Crew Mgmt",
                    "Inventory",
                    "Expenses",
                    "HRMS",
                    "Analytics",
                    "SMS/WhatsApp"
                  ].map((tag) => (
                    <span
                      key={tag}
                      style={{
                        background: d.primaryCont,
                        color: d.primary,
                        padding: "4px 12px",
                        borderRadius: "10px",
                        fontSize: "12px",
                        fontWeight: 500
                      }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
              <div
                style={{
                  ...card({ padding: "36px", boxShadow: d.sh4 }),
                  borderColor: d.primary
                }}
              >
                <div
                  style={{
                    width: "60px",
                    height: "60px",
                    borderRadius: "18px",
                    background: d.primaryCont,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: "20px"
                  }}
                >
                  <Store size={30} color={d.primary} />
                </div>
                <h3
                  style={{
                    fontSize: "22px",
                    fontWeight: 700,
                    color: d.text,
                    marginBottom: "8px"
                  }}
                >
                  UtilStop Marketplace
                </h3>
                <p
                  style={{
                    fontSize: "15px",
                    color: d.textSec,
                    lineHeight: 1.7,
                    marginBottom: "20px"
                  }}
                >
                  Optional open marketplace generating customer demand and
                  additional booking opportunities.
                </p>
                <div
                  style={{
                    padding: "14px 18px",
                    borderRadius: "12px",
                    background: d.greenBg,
                    display: "flex",
                    alignItems: "center",
                    gap: "10px"
                  }}
                >
                  <CheckCircle size={18} color={d.greenText} />
                  <span
                    style={{
                      fontSize: "13px",
                      color: d.greenText,
                      fontWeight: 500
                    }}
                  >
                    Up to <strong>20% additional booking opportunities</strong>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── UTILSTOP MARKETPLACE ── */}
        <section
          style={{
            padding: isMobile ? "64px 16px" : "88px 24px",
            background: d.bg
          }}
        >
          <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                gap: isMobile ? "40px" : "64px",
                alignItems: "center"
              }}
            >
              {/* Left — Copy block */}
              <div>
                <div style={pill}>UtilStop Marketplace</div>
                <h2
                  style={{
                    fontSize: isMobile ? "26px" : "clamp(26px,3vw,38px)",
                    fontWeight: 700,
                    color: d.text,
                    margin: "0 0 18px",
                    lineHeight: 1.25
                  }}
                >
                  Grow Beyond Your
                  <br />
                  Existing Customer Base
                </h2>
                <p
                  style={{
                    fontSize: "15px",
                    color: d.textSec,
                    lineHeight: 1.85,
                    marginBottom: "14px"
                  }}
                >
                  EzeitPro works seamlessly with UtilStop Marketplace — a
                  dedicated service discovery platform where high-intent
                  customers search, compare, and book local services.
                </p>
                <p
                  style={{
                    fontSize: "15px",
                    color: d.textSec,
                    lineHeight: 1.85,
                    marginBottom: "28px"
                  }}
                >
                  List your business once on EzeitPro and instantly become
                  visible to thousands of customers actively looking for exactly
                  what you offer — no extra setup required.
                </p>
                <a
                  href="https://www.utilstop.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    ...btnP,
                    textDecoration: "none",
                    display: "inline-flex"
                  }}
                >
                  Explore UtilStop Marketplace <ArrowRight size={16} />
                </a>
                <p
                  style={{
                    fontSize: "13px",
                    color: d.textMuted,
                    marginTop: "14px"
                  }}
                >
                  🌏&nbsp; Currently available in{" "}
                  <strong style={{ color: d.textSec }}>India</strong> and{" "}
                  <strong style={{ color: d.textSec }}>UAE</strong>{" "}
                  &nbsp;·&nbsp; More countries coming soon
                </p>
              </div>

              {/* Right — Benefit cards 2×2 */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 1fr)",
                  gap: "16px"
                }}
              >
                {[
                  {
                    Icon: BsGlobe2,
                    title: "Expand Your Market",
                    desc: "Get discovered by thousands of customers already browsing UtilStop — beyond your walk-in and referral base."
                  },
                  {
                    Icon: BsPeopleFill,
                    title: "Reach Ready-to-Book Customers",
                    desc: "UtilStop surfaces your services to high-intent buyers actively searching for what you offer, in your city."
                  },
                  {
                    Icon: BsCashStack,
                    title: "Reduce Marketing Spend",
                    desc: "Replace costly ad campaigns with organic marketplace discovery. Lower your customer acquisition cost significantly."
                  },
                  {
                    Icon: BsGraphUpArrow,
                    title: "Scale Across Borders",
                    desc: "Available today in India and UAE. As UtilStop expands to new markets, your business grows with it — automatically."
                  }
                ].map(({ Icon, title, desc }) => (
                  <div
                    key={title}
                    style={card({ padding: "20px" })}
                    onMouseEnter={(e) => hoverCard(e, true)}
                    onMouseLeave={(e) => hoverCard(e, false)}
                  >
                    <Icon
                      size={24}
                      color={d.primary}
                      style={{ marginBottom: "10px", display: "block" }}
                    />
                    <div
                      style={{
                        fontSize: "14px",
                        fontWeight: 600,
                        color: d.text,
                        marginBottom: "6px",
                        lineHeight: 1.3
                      }}
                    >
                      {title}
                    </div>
                    <div
                      style={{
                        fontSize: "13px",
                        color: d.textSec,
                        lineHeight: 1.75
                      }}
                    >
                      {desc}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── PRICING ── */}
        <section
          id="pricing"
          style={{ padding: "88px 24px", background: d.bg }}
        >
          <style>{`
          @keyframes priceFadeUp { from { opacity:0; transform:translateY(20px); } to { opacity:1; transform:translateY(0); } }
          @keyframes priceScaleIn { from { opacity:0; transform:scale(0.96); } to { opacity:1; transform:scale(1); } }
          @keyframes badgeFloat { 0%,100%{transform:translateX(-50%) translateY(0);} 50%{transform:translateX(-50%) translateY(-5px);} }
          .p-card { transition: all 0.35s cubic-bezier(0.23,1,0.32,1); }
          .p-card:hover { transform: translateY(-6px) !important; }
          .p-card.p-featured:hover { transform: translateY(-10px) !important; }
          .p-cta:hover { transform: translateY(-2px); filter: brightness(1.08); }
          .p-feat-row:hover { padding-left: 6px !important; }
        `}</style>

          <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
            {/* Header */}
            <div style={{ textAlign: "center", marginBottom: "48px" }}>
              <div style={pill}>Simple, Transparent Pricing</div>
              <h2
                style={{
                  fontSize: isMobile ? "32px" : "clamp(32px,4vw,52px)",
                  fontWeight: 700,
                  lineHeight: 1.15,
                  marginBottom: "14px"
                }}
              >
                Choose the plan that
                <br />
                <span
                  style={{
                    color: d.primary,
                    fontStyle: "italic",
                    fontWeight: 300
                  }}
                >
                  scales with you
                </span>
              </h2>
              <p
                style={{
                  fontSize: "17px",
                  color: d.textSec,
                  maxWidth: "520px",
                  margin: "0 auto",
                  lineHeight: 1.7
                }}
              >
                From solo operators to enterprise fleets — manage orders, crew,
                and customer communication from one platform.
              </p>
            </div>

            {/* Controls: Country + Toggle */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "20px",
                marginBottom: "40px"
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "6px"
                }}
              >
                <label
                  style={{
                    fontSize: "12px",
                    color: d.textMuted,
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em"
                  }}
                >
                  Select Country
                </label>
                <select
                  value={pricingCountry}
                  onChange={(e) => setPricingCountry(e.target.value)}
                  style={{
                    padding: "10px 14px",
                    borderRadius: "10px",
                    border: `1.5px solid ${d.borderMd}`,
                    fontSize: "14px",
                    background: d.surface,
                    color: d.text,
                    cursor: "pointer",
                    outline: "none",
                    minWidth: "160px"
                  }}
                >
                  <option value="IN">India</option>
                  <option value="AE">UAE</option>
                  <option value="US">USA</option>
                </select>
              </div>
              <div
                style={{ display: "flex", alignItems: "center", gap: "12px" }}
              >
                <span
                  style={{
                    fontSize: "14px",
                    color: d.text,
                    fontWeight: annual ? 400 : 600,
                    opacity: annual ? 0.5 : 1,
                    display: "flex",
                    alignItems: "center",
                    gap: "8px"
                  }}
                >
                  Monthly
                  {!annual && (
                    <span
                      style={{
                        fontSize: "11px",
                        fontWeight: 600,
                        background: "#fef3c7",
                        color: "#d97706",
                        padding: "3px 8px",
                        borderRadius: "20px"
                      }}
                    >
                      Intro Offer! Save 50%
                    </span>
                  )}
                </span>
                <div
                  onClick={() => setAnnual(!annual)}
                  style={{
                    width: "46px",
                    height: "24px",
                    borderRadius: "12px",
                    background: annual ? d.primary : d.borderMd,
                    cursor: "pointer",
                    position: "relative",
                    transition: "background 0.3s",
                    flexShrink: 0
                  }}
                >
                  <div
                    style={{
                      width: "20px",
                      height: "20px",
                      borderRadius: "50%",
                      background: "#fff",
                      position: "absolute",
                      top: "2px",
                      left: "2px",
                      boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
                      transition: "transform 0.3s",
                      transform: annual ? "translateX(22px)" : "translateX(0)"
                    }}
                  />
                </div>
                <span
                  style={{
                    fontSize: "14px",
                    color: d.text,
                    fontWeight: annual ? 600 : 400,
                    opacity: annual ? 1 : 0.5,
                    display: "flex",
                    alignItems: "center",
                    gap: "8px"
                  }}
                >
                  Annual
                  {annual && (
                    <span
                      style={{
                        fontSize: "11px",
                        fontWeight: 600,
                        background: "#dcfce7",
                        color: "#16a34a",
                        padding: "3px 8px",
                        borderRadius: "20px"
                      }}
                    >
                      Save Additional 20%
                    </span>
                  )}
                </span>
              </div>
            </div>

            {/* Plans Grid */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "repeat(3,1fr)",
                gap: "24px",
                alignItems: "start",
                marginBottom: "56px"
              }}
            >
              {pricingPlans.map((plan, i) => {
                const isCurrentPlan = currentPlan
                  ? plan.id === currentPlan
                  : false;
                const isFeatured =
                  isCurrentPlan || (!currentPlan && plan.id === "pro");
                const displayPrice = getPlanPrice(plan);
                return (
                  <div
                    key={plan.id}
                    className={`p-card${isFeatured ? " p-featured" : ""}`}
                    onMouseEnter={() => setHoveredPlan(plan.id)}
                    onMouseLeave={() => setHoveredPlan(null)}
                    style={{
                      background: d.surface,
                      borderRadius: "20px",
                      padding: "36px 28px 28px",
                      display: "flex",
                      flexDirection: "column",
                      position: "relative",
                      minHeight: "520px",
                      border: isFeatured
                        ? `2px solid ${d.primary}`
                        : `1.5px solid ${d.border}`,
                      boxShadow: isFeatured ? d.sh8 : d.sh2,
                      animation: `priceScaleIn 0.5s ease ${0.1 + i * 0.1}s both`
                    }}
                  >
                    {isFeatured && (
                      <div
                        style={{
                          position: "absolute",
                          top: "-14px",
                          left: "50%",
                          transform: "translateX(-50%)",
                          background: d.primary,
                          color: "#fff",
                          fontSize: "12px",
                          fontWeight: 700,
                          padding: "6px 18px",
                          borderRadius: "20px",
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          whiteSpace: "nowrap",
                          boxShadow: `0 4px 12px ${isDark ? "rgba(233,30,99,0.5)" : "rgba(194,24,91,0.35)"}`,
                          animation: "badgeFloat 3s ease-in-out infinite"
                        }}
                      >
                        {isCurrentPlan
                          ? "✓ Your Current Plan"
                          : `★ ${plan.badge}`}
                      </div>
                    )}

                    <div style={{ marginBottom: "20px" }}>
                      {isFeatured ? (
                        <span
                          style={{
                            background: d.primary,
                            color: "#fff",
                            padding: "4px 12px",
                            borderRadius: "8px",
                            fontSize: "14px",
                            fontWeight: 700,
                            display: "inline-block",
                            marginBottom: "6px"
                          }}
                        >
                          {plan.name}
                        </span>
                      ) : (
                        <span
                          style={{
                            fontSize: "20px",
                            fontWeight: 700,
                            color: d.text,
                            display: "block",
                            marginBottom: "4px"
                          }}
                        >
                          {plan.name}
                        </span>
                      )}
                      <span style={{ fontSize: "13px", color: d.textMuted }}>
                        {plan.tagline}
                      </span>
                    </div>

                    <div style={{ marginBottom: "20px" }}>
                      {plan.originalPrice && !annual && (
                        <span
                          style={{
                            fontSize: "15px",
                            color: d.textMuted,
                            textDecoration: "line-through",
                            display: "block",
                            marginBottom: "2px"
                          }}
                        >
                          {getOriginalPrice(pricingCountry)}
                        </span>
                      )}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "baseline",
                          gap: "4px"
                        }}
                      >
                        <span
                          style={{
                            fontSize: plan.price === "Custom" ? "30px" : "44px",
                            fontWeight: 700,
                            color: d.text,
                            lineHeight: 1
                          }}
                        >
                          {displayPrice}
                        </span>
                        {plan.period && (
                          <span
                            style={{ fontSize: "14px", color: d.textMuted }}
                          >
                            {plan.period}
                          </span>
                        )}
                      </div>
                      {plan.id === "pro" && annual && (
                        <span
                          style={{
                            fontSize: "12px",
                            color: d.greenText,
                            fontWeight: 500,
                            marginTop: "3px",
                            display: "block"
                          }}
                        >
                          Billed annually at{" "}
                          {getAnnualBilledNote(pricingCountry)}
                        </span>
                      )}
                    </div>

                    <div
                      style={{
                        height: "1px",
                        background: d.border,
                        marginBottom: "20px"
                      }}
                    />

                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                        marginBottom: "20px",
                        flex: 1
                      }}
                    >
                      {plan.features.map((f, fi) => (
                        <div
                          key={fi}
                          className="p-feat-row"
                          style={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: "10px",
                            opacity: f.included ? 1 : 0.38,
                            borderRadius: "8px",
                            padding: "2px 0",
                            transition: "padding 0.2s"
                          }}
                        >
                          <span
                            style={{
                              flexShrink: 0,
                              marginTop: "1px",
                              color: f.included
                                ? f.highlight
                                  ? "#16a34a"
                                  : d.primary
                                : d.textMuted
                            }}
                          >
                            {f.included ? (
                              <svg
                                width="18"
                                height="18"
                                viewBox="0 0 18 18"
                                fill="none"
                              >
                                <circle
                                  cx="9"
                                  cy="9"
                                  r="9"
                                  fill="currentColor"
                                  opacity="0.12"
                                />
                                <path
                                  d="M5.5 9.2L7.8 11.5L12.5 6.5"
                                  stroke="currentColor"
                                  strokeWidth="1.8"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            ) : (
                              <svg
                                width="18"
                                height="18"
                                viewBox="0 0 18 18"
                                fill="none"
                              >
                                <circle
                                  cx="9"
                                  cy="9"
                                  r="9"
                                  fill="currentColor"
                                  opacity="0.08"
                                />
                                <path
                                  d="M6.5 6.5L11.5 11.5M11.5 6.5L6.5 11.5"
                                  stroke="currentColor"
                                  strokeWidth="1.6"
                                  strokeLinecap="round"
                                />
                              </svg>
                            )}
                          </span>
                          <span
                            style={{
                              fontSize: "13px",
                              color: d.textSec,
                              lineHeight: 1.5,
                              display: "flex",
                              alignItems: "center",
                              flexWrap: "wrap",
                              gap: "5px"
                            }}
                          >
                            {f.whatsapp && (
                              <span
                                style={{
                                  color: "#25D366",
                                  display: "inline-flex"
                                }}
                              >
                                <svg
                                  width="14"
                                  height="14"
                                  viewBox="0 0 24 24"
                                  fill="currentColor"
                                >
                                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
                                  <path d="M12 0C5.373 0 0 5.373 0 12c0 2.625.846 5.059 2.284 7.034L.789 23.492a.5.5 0 00.612.638l4.725-1.217A11.95 11.95 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-2.352 0-4.55-.683-6.406-1.86l-.252-.163-3.048.785.822-2.998-.177-.265A9.954 9.954 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z" />
                                </svg>
                              </span>
                            )}
                            {f.text}
                            {f.note && (
                              <span
                                style={{
                                  fontSize: "11px",
                                  color: d.textMuted,
                                  fontStyle: "italic"
                                }}
                              >
                                · {f.note}
                              </span>
                            )}
                          </span>
                        </div>
                      ))}
                    </div>

                    {plan.footnote && (
                      <div
                        style={{
                          fontSize: "12px",
                          color: d.textMuted,
                          background: d.bgAlt,
                          padding: "10px 14px",
                          borderRadius: "10px",
                          marginBottom: "16px",
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          lineHeight: 1.5
                        }}
                      >
                        <span style={{ opacity: 0.6 }}>ℹ</span> {plan.footnote}
                      </div>
                    )}

                    {plan.id !== "pro" && (
                      <button
                        onClick={() =>
                          plan.cta === "Contact Sales"
                            ? openWhatsAppChat()
                            : navigate("/login")
                        }
                        className="p-cta"
                        style={{
                          width: "100%",
                          padding: "13px 20px",
                          borderRadius: "12px",
                          fontSize: "14px",
                          fontWeight: 600,
                          cursor: "pointer",
                          border: "none",
                          background: d.primary,
                          color: "#fff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "8px",
                          transition: "all 0.25s",
                          boxShadow: `0 4px 12px ${isDark ? "rgba(233,30,99,0.4)" : "rgba(194,24,91,0.25)"}`,
                          marginTop: "auto"
                        }}
                      >
                        {plan.cta}
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                        >
                          <path
                            d="M3 8H13M13 8L9 4M13 8L9 12"
                            stroke="currentColor"
                            strokeWidth="1.6"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Trust Strip */}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "center",
                alignItems: "center",
                gap: "20px",
                padding: "28px 20px",
                borderRadius: "16px",
                background: d.bgAlt,
                marginBottom: "56px"
              }}
            >
              {[
                ["🔒", "256-bit encryption"],
                ["⚡", "99.9% uptime SLA"],
                ["↩️", "Cancel anytime"],
                ["💬", "Priority support"]
              ].map(([icon, label], i) => (
                <React.Fragment key={i}>
                  {i > 0 && (
                    <div
                      style={{
                        width: "4px",
                        height: "4px",
                        borderRadius: "50%",
                        background: d.border
                      }}
                    />
                  )}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      fontSize: "14px",
                      color: d.textSec,
                      fontWeight: 500
                    }}
                  >
                    <span>{icon}</span>
                    <span>{label}</span>
                  </div>
                </React.Fragment>
              ))}
            </div>

            {/* Enterprise suite call-out */}
            <div
              style={{
                ...card({ border: `1.5px solid ${d.primary}` }),
                padding: isMobile ? "28px 22px" : "36px 40px",
                marginBottom: "56px",
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "1.2fr 1fr",
                gap: "28px",
                alignItems: "center"
              }}
            >
              <div>
                <div style={{ marginBottom: "12px" }}>
                  <EnterpriseBadge d={d} />
                </div>
                <h3
                  style={{
                    fontSize: isMobile ? "22px" : "28px",
                    fontWeight: 700,
                    color: d.text,
                    margin: "0 0 10px"
                  }}
                >
                  Dealership &amp; Finance suite
                </h3>
                <p
                  style={{
                    fontSize: "15px",
                    color: d.textSec,
                    lineHeight: 1.7,
                    margin: "0 0 20px"
                  }}
                >
                  Set up and priced for your business. Tell us about your
                  showroom, workshop and stores and we will send a quote.
                </p>
                <button style={btnP} onClick={goToContact}>
                  Contact us <ArrowRight size={15} />
                </button>
              </div>
              <ul
                style={{
                  listStyle: "none",
                  margin: 0,
                  padding: 0,
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px"
                }}
              >
                {ENTERPRISE_SUITE_FEATURES.map((f) => (
                  <li
                    key={f.text}
                    style={{
                      display: "flex",
                      gap: "10px",
                      alignItems: "center",
                      fontSize: "14px",
                      color: d.text,
                      fontWeight: 500
                    }}
                  >
                    <CheckCircle
                      size={18}
                      color={d.primary}
                      style={{ flexShrink: 0 }}
                    />
                    {f.text}
                  </li>
                ))}
              </ul>
            </div>

            {/* Comparison Table */}
            <div style={{ marginBottom: "56px" }}>
              <h3
                style={{
                  fontSize: "26px",
                  fontWeight: 700,
                  textAlign: "center",
                  marginBottom: "28px",
                  color: d.text
                }}
              >
                Full feature comparison
              </h3>
              <div
                style={{
                  borderRadius: "16px",
                  overflow: "hidden",
                  border: `1.5px solid ${d.border}`,
                  background: d.surface
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "2fr 1fr 1fr 1fr",
                    background: d.primary,
                    color: "#fff"
                  }}
                >
                  {["Features", "Starter", "Pro", "Enterprise"].map((h, i) => (
                    <div
                      key={i}
                      style={{
                        padding: "14px 20px",
                        fontSize: "13px",
                        fontWeight: 600,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: i > 0 ? "center" : "flex-start"
                      }}
                    >
                      {h}
                    </div>
                  ))}
                </div>
                {COMPARISON_ROWS.map(([feat, s, p, e], ri) => (
                  <div
                    key={ri}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "2fr 1fr 1fr 1fr",
                      borderBottom: `1px solid ${d.border}`,
                      background:
                        ri % 2 === 0
                          ? "transparent"
                          : isDark
                            ? "rgba(255,255,255,0.02)"
                            : "rgba(0,0,0,0.015)"
                    }}
                  >
                    {[feat, s, p, e].map((val, ci) => (
                      <div
                        key={ci}
                        style={{
                          padding: "13px 20px",
                          fontSize: "13px",
                          color:
                            val === "—"
                              ? d.textMuted
                              : ci === 2
                                ? d.primary
                                : d.textSec,
                          fontWeight:
                            ci === 0
                              ? 500
                              : ci === 2 && val !== "—"
                                ? 600
                                : 400,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: ci > 0 ? "center" : "flex-start",
                          background:
                            ci === 2
                              ? isDark
                                ? "rgba(233,30,99,0.05)"
                                : "rgba(194,24,91,0.04)"
                              : "transparent"
                        }}
                      >
                        {val}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>

            {/* FAQ */}
            <div style={{ maxWidth: "680px", margin: "0 auto 56px" }}>
              <h3
                style={{
                  fontSize: "26px",
                  fontWeight: 700,
                  textAlign: "center",
                  marginBottom: "28px",
                  color: d.text
                }}
              >
                Common questions
              </h3>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px"
                }}
              >
                {pricingFaqs.map((faq, i) => (
                  <div
                    key={i}
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    style={{
                      border: `1.5px solid ${openFaq === i ? d.primary : d.border}`,
                      borderRadius: "14px",
                      padding: "18px 22px",
                      cursor: "pointer",
                      background: d.surface,
                      transition: "border-color 0.25s"
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: "16px"
                      }}
                    >
                      <span
                        style={{
                          fontSize: "15px",
                          fontWeight: 600,
                          color: d.text,
                          lineHeight: 1.4
                        }}
                      >
                        {faq.q}
                      </span>
                      <span
                        style={{
                          fontSize: "22px",
                          fontWeight: 300,
                          color: d.textMuted,
                          flexShrink: 0,
                          transform: openFaq === i ? "rotate(45deg)" : "none",
                          transition: "transform 0.3s"
                        }}
                      >
                        +
                      </span>
                    </div>
                    {openFaq === i && (
                      <p
                        style={{
                          fontSize: "14px",
                          lineHeight: 1.75,
                          color: d.textSec,
                          marginTop: "12px"
                        }}
                      >
                        {faq.a}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* CTA Banner */}
            <div
              style={{
                borderRadius: "24px",
                background: d.primary,
                padding: isMobile ? "40px 24px" : "56px 40px",
                textAlign: "center",
                position: "relative",
                overflow: "hidden"
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: "-40px",
                  right: "-40px",
                  width: "200px",
                  height: "200px",
                  borderRadius: "50%",
                  background: "rgba(255,255,255,0.07)"
                }}
              />
              <div
                style={{
                  position: "absolute",
                  bottom: "-60px",
                  left: "-20px",
                  width: "240px",
                  height: "240px",
                  borderRadius: "50%",
                  background: "rgba(255,255,255,0.05)"
                }}
              />
              <h3
                style={{
                  fontSize: isMobile ? "24px" : "32px",
                  fontWeight: 700,
                  color: "#fff",
                  marginBottom: "10px",
                  position: "relative"
                }}
              >
                Ready to streamline your operations?
              </h3>
              <p
                style={{
                  fontSize: "15px",
                  color: "rgba(255,255,255,0.7)",
                  maxWidth: "460px",
                  margin: "0 auto 28px",
                  lineHeight: 1.65,
                  position: "relative"
                }}
              >
                Join thousands of businesses managing orders, crew, and
                customers — all in one place.
              </p>
              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  justifyContent: "center",
                  flexWrap: "wrap",
                  position: "relative"
                }}
              >
                <button
                  onClick={() => navigate("/login")}
                  style={{
                    padding: "13px 32px",
                    borderRadius: "12px",
                    background: "#fff",
                    color: d.primary,
                    fontWeight: 700,
                    fontSize: "14px",
                    border: "none",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px"
                  }}
                >
                  Start free trial{" "}
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M3 8H13M13 8L9 4M13 8L9 12"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <button
                  onClick={openWhatsAppChat}
                  style={{
                    padding: "13px 32px",
                    borderRadius: "12px",
                    background: "transparent",
                    color: "#fff",
                    fontWeight: 500,
                    fontSize: "14px",
                    border: "1.5px solid rgba(255,255,255,0.3)",
                    cursor: "pointer"
                  }}
                >
                  Talk to sales
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* ── VIDEO LIGHTBOX MODAL ── */}
        {showVideoModal && (
          <>
            <style>{`
            @keyframes videoBackdropIn { from { opacity: 0; } to { opacity: 1; } }
            @keyframes videoPlayerIn { from { opacity: 0; transform: scale(0.9) translateY(24px); } to { opacity: 1; transform: scale(1) translateY(0); } }
            @keyframes videoDotPulse { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.4; transform: scale(0.6); } }
            .video-close-btn:hover { background: rgba(255,255,255,0.22) !important; transform: scale(1.1); }
          `}</style>
            <div
              onClick={() => setShowVideoModal(false)}
              style={{
                position: "fixed",
                inset: 0,
                zIndex: 9900,
                background: "rgba(0, 0, 0, 0.90)",
                backdropFilter: "blur(14px)",
                WebkitBackdropFilter: "blur(14px)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: isMobile ? "16px" : "32px",
                animation: "videoBackdropIn 0.22s ease"
              }}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  position: "relative",
                  width: "100%",
                  maxWidth: "960px",
                  animation:
                    "videoPlayerIn 0.32s cubic-bezier(0.23, 1, 0.32, 1)"
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "14px"
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px"
                    }}
                  >
                    <div
                      style={{
                        width: "8px",
                        height: "8px",
                        borderRadius: "50%",
                        background: "#FF4444",
                        animation: "videoDotPulse 2s ease-in-out infinite"
                      }}
                    />
                    <span
                      style={{
                        color: "rgba(255,255,255,0.55)",
                        fontSize: "11px",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.12em"
                      }}
                    >
                      Product Demo
                    </span>
                  </div>
                  <button
                    className="video-close-btn"
                    onClick={() => setShowVideoModal(false)}
                    style={{
                      background: "rgba(255,255,255,0.1)",
                      border: "1px solid rgba(255,255,255,0.18)",
                      borderRadius: "50%",
                      width: "38px",
                      height: "38px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      color: "#fff",
                      fontSize: "16px",
                      lineHeight: 1,
                      transition: "all 0.2s ease",
                      flexShrink: 0
                    }}
                    title="Close (Esc)"
                  >
                    ✕
                  </button>
                </div>
                <div
                  style={{
                    position: "relative",
                    paddingBottom: "56.25%",
                    height: 0,
                    borderRadius: isMobile ? "12px" : "18px",
                    overflow: "hidden",
                    boxShadow:
                      "0 32px 80px rgba(0,0,0,0.65), 0 0 0 1px rgba(255,255,255,0.07)"
                  }}
                >
                  <iframe
                    src="https://www.youtube.com/embed/HkSNeh7EDGQ?autoplay=1&rel=0&modestbranding=1&color=white"
                    title="See How EzeitPro Works"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      border: "none"
                    }}
                  />
                </div>
                <div
                  style={{
                    marginTop: "14px",
                    textAlign: "center",
                    color: "rgba(255,255,255,0.25)",
                    fontSize: "12px",
                    letterSpacing: "0.03em"
                  }}
                >
                  Press{" "}
                  <kbd
                    style={{
                      background: "rgba(255,255,255,0.1)",
                      border: "1px solid rgba(255,255,255,0.15)",
                      borderRadius: "4px",
                      padding: "1px 6px",
                      fontSize: "11px",
                      fontFamily: "monospace"
                    }}
                  >
                    ESC
                  </kbd>{" "}
                  or click outside to close
                </div>
              </div>
            </div>
          </>
        )}

        {/* ── WHY CHOOSE / CTA ── */}
        <section
          id="contact"
          style={{ padding: "88px 24px", background: d.ctaBg }}
        >
          <div
            style={{
              maxWidth: "1280px",
              margin: "0 auto",
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              gap: "56px",
              alignItems: "start"
            }}
          >
            <div>
              <div style={pill}>Why Choose EzeitPro</div>
              <h2
                style={{
                  fontSize: isMobile ? "30px" : "clamp(30px,3.5vw,48px)",
                  fontWeight: 700,
                  marginBottom: "14px",
                  lineHeight: 1.2
                }}
              >
                From Chaos to Control.
                <br />
                From Control to Growth.
              </h2>
              <p
                style={{
                  fontSize: "16px",
                  color: d.textSec,
                  lineHeight: 1.75,
                  marginBottom: "28px"
                }}
              >
                Everything service businesses need to go from scattered to
                streamlined — in hours, not months.
              </p>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px"
                }}
              >
                {whyChoose.map((w, i) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "12px",
                      padding: "14px 16px",
                      borderRadius: "12px",
                      background: d.surface,
                      border: `1px solid ${d.border}`
                    }}
                  >
                    <div
                      style={{
                        width: "28px",
                        height: "28px",
                        borderRadius: "8px",
                        background: d.primaryCont,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0
                      }}
                    >
                      <span
                        style={{
                          fontSize: "11px",
                          fontWeight: 700,
                          color: d.primary
                        }}
                      >
                        {w.num}
                      </span>
                    </div>
                    <span
                      style={{
                        fontSize: "14px",
                        color: d.text,
                        lineHeight: 1.5
                      }}
                    >
                      {w.text}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div style={card({ padding: "36px", boxShadow: d.sh8 })}>
              <h3
                style={{
                  fontSize: "26px",
                  fontWeight: 700,
                  color: d.text,
                  marginBottom: "8px"
                }}
              >
                Book a Demo Today
              </h3>
              <p
                style={{
                  fontSize: "15px",
                  color: d.textSec,
                  lineHeight: 1.7,
                  marginBottom: "28px"
                }}
              >
                See EzeitPro in action and discover how quickly your business
                can go from scattered to streamlined.
              </p>
              <div
                style={{ display: "grid", gap: "12px", marginBottom: "24px" }}
              >
                <input
                  value={demoForm.name}
                  onChange={(e) =>
                    setDemoForm((prev) => ({ ...prev, name: e.target.value }))
                  }
                  placeholder="Your name"
                  style={{
                    width: "100%",
                    padding: "14px 16px",
                    fontSize: "14px",
                    borderRadius: "12px",
                    border: `1.5px solid ${d.border}`,
                    background: d.surface,
                    color: d.text,
                    outline: "none"
                  }}
                />
                <input
                  value={demoForm.email}
                  onChange={(e) =>
                    setDemoForm((prev) => ({ ...prev, email: e.target.value }))
                  }
                  placeholder="Your email"
                  type="email"
                  style={{
                    width: "100%",
                    padding: "14px 16px",
                    fontSize: "14px",
                    borderRadius: "12px",
                    border: `1.5px solid ${d.border}`,
                    background: d.surface,
                    color: d.text,
                    outline: "none"
                  }}
                />
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                    gap: "12px"
                  }}
                >
                  <input
                    value={demoForm.date}
                    onChange={(e) =>
                      setDemoForm((prev) => ({ ...prev, date: e.target.value }))
                    }
                    placeholder="Preferred date"
                    type="date"
                    style={{
                      width: "100%",
                      padding: "14px 16px",
                      fontSize: "14px",
                      borderRadius: "12px",
                      border: `1.5px solid ${d.border}`,
                      background: d.surface,
                      color: d.text,
                      outline: "none"
                    }}
                  />
                  <input
                    value={demoForm.time}
                    onChange={(e) =>
                      setDemoForm((prev) => ({ ...prev, time: e.target.value }))
                    }
                    placeholder="Preferred time"
                    type="time"
                    style={{
                      width: "100%",
                      padding: "14px 16px",
                      fontSize: "14px",
                      borderRadius: "12px",
                      border: `1.5px solid ${d.border}`,
                      background: d.surface,
                      color: d.text,
                      outline: "none"
                    }}
                  />
                </div>
                <textarea
                  value={demoForm.message}
                  onChange={(e) =>
                    setDemoForm((prev) => ({
                      ...prev,
                      message: e.target.value
                    }))
                  }
                  placeholder="Tell us about your business or goals"
                  rows={4}
                  style={{
                    width: "100%",
                    padding: "14px 16px",
                    fontSize: "14px",
                    borderRadius: "12px",
                    border: `1.5px solid ${d.border}`,
                    background: d.surface,
                    color: d.text,
                    outline: "none",
                    resize: "vertical"
                  }}
                />
              </div>
              {demoSubmitted && (
                <div
                  style={{
                    padding: "14px 16px",
                    borderRadius: "14px",
                    background: d.greenBg,
                    color: d.greenText,
                    fontWeight: 600
                  }}
                >
                  Thanks! Your demo request has been submitted. We will reach
                  out shortly.
                </div>
              )}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                  marginBottom: "28px"
                }}
              >
                <button
                  onClick={handleDemoSubmit}
                  disabled={demoSubmitting}
                  style={{
                    ...btnP,
                    justifyContent: "center",
                    width: "100%",
                    boxSizing: "border-box",
                    opacity: demoSubmitting ? 0.7 : 1
                  }}
                >
                  {demoSubmitting ? "Sending..." : "Schedule a Demo"}{" "}
                  <ArrowRight size={15} />
                </button>
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                  paddingTop: "20px",
                  borderTop: `1px solid ${d.border}`
                }}
              >
                {[
                  { label: "Email", value: "sales@ezeitpro.com" },
                  { label: "Website", value: "www.ezeitpro.com" },
                  { label: "Phone", value: "+971-561303601" }
                ].map((c, i) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      gap: "12px",
                      padding: "11px 14px",
                      borderRadius: "10px",
                      background: d.bgAlt,
                      border: `1px solid ${d.border}`
                    }}
                  >
                    <span
                      style={{
                        fontSize: "12px",
                        fontWeight: 600,
                        color: d.textMuted,
                        minWidth: "52px"
                      }}
                    >
                      {c.label}
                    </span>
                    <span
                      style={{
                        fontSize: "13px",
                        color: d.primary,
                        fontWeight: 500
                      }}
                    >
                      {c.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── FOOTER ── */}
        <footer
          style={{
            background: d.footBg,
            color: "#E0E0E0",
            padding: "56px 24px 28px"
          }}
        >
          <div style={{ maxWidth: "1280px", margin: "0 auto" }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr 1fr" : "2fr 1fr 1fr 1fr",
                gap: "40px",
                marginBottom: "40px"
              }}
            >
              <div style={{ gridColumn: isMobile ? "1 / -1" : "auto" }}>
                <BrandLogo
                  height={36}
                  tone="dark"
                  style={{ marginBottom: "14px" }}
                />
                <p
                  style={{
                    fontSize: "13px",
                    color: "#9E9E9E",
                    lineHeight: 1.7,
                    maxWidth: "300px"
                  }}
                >
                  The all-in-one platform for service businesses. Simplify
                  operations, delight customers, and grow faster.
                </p>
                <div
                  className="footer-social"
                  style={{ marginTop: "1rem", color: d.primary }}
                >
                  <a
                    href="https://www.instagram.com/ezeit_pro/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-icon"
                    aria-label="Instagram"
                  >
                    <BsInstagram size={18} />
                  </a>
                  <a
                    href="https://www.facebook.com/profile.php?id=61589841952735"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-icon"
                    aria-label="Facebook"
                  >
                    <BsFacebook size={18} />
                  </a>
                  <a
                    href="https://www.linkedin.com/company/ezeitpro/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-icon"
                    aria-label="LinkedIn"
                  >
                    <BsLinkedin size={18} />
                  </a>
                  <a
                    href="https://www.youtube.com/@EzeitPro"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-icon"
                    aria-label="YouTube"
                  >
                    <BsYoutube size={18} />
                  </a>
                  <a
                    href="https://x.com/ezeitpro"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-icon"
                    aria-label="X"
                  >
                    <BsTwitterX size={18} />
                  </a>
                  <a
                    href="https://wa.me/971561303601"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-icon"
                    aria-label="WhatsApp"
                  >
                    <BsWhatsapp size={18} />
                  </a>
                </div>
              </div>
              {[
                {
                  title: "Company",
                  links: [
                    { label: "About", to: "/" },
                    { label: "Contact", to: "/contact" },
                    { label: "Careers", to: "/" }
                  ]
                },
                {
                  title: "Legal",
                  links: [
                    { label: "Privacy", to: "/privacy-policy" },
                    { label: "Terms", to: "/terms" },
                    { label: "Security", to: "/security" }
                  ]
                }
              ].map((col, i) => (
                <div key={i}>
                  <h4
                    style={{
                      fontSize: "12px",
                      fontWeight: 600,
                      color: "#FFF",
                      marginBottom: "14px",
                      textTransform: "uppercase",
                      letterSpacing: "0.08em"
                    }}
                  >
                    {col.title}
                  </h4>
                  {col.links.map((link) =>
                    link.to.startsWith("#") ? (
                      <a
                        key={link.label}
                        href={link.to}
                        style={{
                          display: "block",
                          color: "#9E9E9E",
                          textDecoration: "none",
                          fontSize: "13px",
                          marginBottom: "9px"
                        }}
                      >
                        {link.label}
                      </a>
                    ) : (
                      <Link
                        key={link.label}
                        to={link.to}
                        style={{
                          display: "block",
                          color: "#9E9E9E",
                          textDecoration: "none",
                          fontSize: "13px",
                          marginBottom: "9px"
                        }}
                      >
                        {link.label}
                      </Link>
                    )
                  )}
                </div>
              ))}
            </div>
            <div
              style={{
                borderTop: "1px solid rgba(255,255,255,0.08)",
                paddingTop: "20px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "12px"
              }}
            >
              <p style={{ fontSize: "12px", color: "#616161" }}>
                © 2026 UtilStop LLC-FZ, Dubai. All rights reserved.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
